import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adSlots";
import { Copy, Check, MapPin, Wifi, Globe2, Clock, Hash, Building2 } from "lucide-react";

export default function MyIPPage() {
  const [geo, setGeo] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/geo")
      .then((r) => setGeo(r.data))
      .catch((e) => setErr(e?.message || "Failed to detect IP"));
  }, []);

  const copy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      trackClick("copy-geo-field", { field: key });
      setCopied(key);
      setTimeout(() => setCopied(null), 1400);
    } catch {}
  };

  const rows = [
    { key: "ip", label: "Public IP", icon: Hash, value: geo?.ip, mono: true, testid: "geo-ip" },
    { key: "isp", label: "ISP / Provider", icon: Building2, value: geo?.isp, testid: "geo-isp" },
    { key: "asn", label: "ASN", icon: Wifi, value: geo?.asn, mono: true, testid: "geo-asn" },
    { key: "city", label: "City", icon: MapPin, value: geo?.city, testid: "geo-city" },
    { key: "region", label: "Region", icon: MapPin, value: geo?.region, testid: "geo-region" },
    { key: "country", label: "Country", icon: Globe2, value: geo ? `${geo.flag || ""} ${geo.country || ""} (${geo.country_code || ""})`.trim() : null, testid: "geo-country" },
    { key: "timezone", label: "Timezone", icon: Clock, value: geo?.timezone, mono: true, testid: "geo-timezone" },
    { key: "postal", label: "Postal code", icon: MapPin, value: geo?.postal, mono: true, testid: "geo-postal" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-10 lg:py-16">
      <header className="mb-8 sm:mb-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ My IP &amp; Location</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">What the internet sees as you</h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
          Your public IP address, Internet Service Provider, and approximate location. Click any value to copy it.
        </p>
      </header>

      {err && (
        <div className="glass rounded-xl p-5 border border-rose-500/30 text-rose-200 mb-6" data-testid="geo-error">
          Couldn't detect your location: {err}
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-4 sm:gap-6">
        {/* Big IP */}
        <div className="md:col-span-2 glass-strong rounded-2xl p-5 sm:p-6 lg:p-8 relative overflow-hidden min-w-0">
          <div className="absolute -top-24 -right-24 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(61,107,255,0.35), transparent)" }} />
          <div className="relative min-w-0">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Your public IP</div>
            <div
              role="button"
              onClick={() => copy("big-ip", geo?.ip)}
              className="font-mono text-xl sm:text-2xl lg:text-3xl font-semibold break-all cursor-pointer group inline-flex items-center gap-3 text-white hover:text-[var(--cobalt-bright)] transition"
              data-testid="geo-ip-big"
            >
              {geo?.ip || "Detecting…"}
              {geo?.ip && (
                copied === "big-ip" ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5 opacity-0 group-hover:opacity-100 transition" />
              )}
            </div>
            <div className="mt-8 text-sm text-[var(--text-dim)]">
              {geo ? (
                <div>
                  <div className="text-white font-medium">{[geo.city, geo.region].filter(Boolean).join(", ")}</div>
                  <div className="text-[var(--text-muted)]">{[geo.country, geo.timezone].filter(Boolean).join(" · ")}</div>
                </div>
              ) : (
                <div className="animate-pulse text-[var(--text-muted)]">Looking up…</div>
              )}
            </div>

            <button
              onClick={() => copy("big-ip", geo?.ip)}
              disabled={!geo?.ip}
              data-testid="geo-copy-ip-btn"
              className="btn-cobalt mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
            >
              {copied === "big-ip" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied === "big-ip" ? "Copied!" : "Copy IP"}
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="md:col-span-3 glass rounded-2xl p-2">
          <div className="divide-y divide-white/5">
            {rows.map((r) => (
              <div
                key={r.key}
                role="button"
                onClick={() => copy(r.key, r.value)}
                className="px-4 py-3.5 flex items-center gap-4 hover:bg-[rgba(61,107,255,0.05)] transition cursor-pointer rounded-lg"
              >
                <div className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center bg-[rgba(61,107,255,0.12)] border border-[rgba(61,107,255,0.22)]">
                  <r.icon className="w-4 h-4 text-[var(--cobalt-bright)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">{r.label}</div>
                  <div className={`truncate text-sm mt-0.5 ${r.mono ? "font-mono" : ""} text-white`} data-testid={r.testid} title={r.value || ""}>
                    {r.value || <span className="text-[var(--text-muted)]">—</span>}
                  </div>
                </div>
                <div className="shrink-0 text-[var(--text-muted)]">
                  {copied === r.key ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 opacity-60" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Map preview */}
      {geo?.latitude && geo?.longitude && (
        <div className="mt-8 glass rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[var(--cobalt-bright)]" />
            <span className="text-sm">Approximate location</span>
            <span className="ml-auto text-xs text-[var(--text-muted)] font-mono">{Number(geo.latitude).toFixed(3)}, {Number(geo.longitude).toFixed(3)}</span>
          </div>
          <iframe
            title="map"
            data-testid="geo-map-iframe"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${geo.longitude - 0.2},${geo.latitude - 0.15},${geo.longitude + 0.2},${geo.latitude + 0.15}&layer=mapnik&marker=${geo.latitude},${geo.longitude}`}
            className="w-full h-72 border-0 grayscale-[30%] contrast-125"
            loading="lazy"
          />
        </div>
      )}

      <p className="mt-8 text-xs text-[var(--text-muted)] font-mono">
        Location is approximate and based on your IP address — not GPS. Accuracy varies by region and ISP.
      </p>

      {/* Ad: bottom */}
      <div className="mt-8">
        <AdSlot slot={AD_SLOTS.myIpBottom} name="my-ip-bottom" minHeight={120} />
      </div>
    </div>
  );
}
